import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Text,
  Flex,
  Badge,
  Spinner,
  SimpleGrid,
  Icon,
  Select,
  HStack,
  VStack,
  Avatar,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip as ChakraTooltip,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Buildings,
  Users,
  MapPin,
  CalendarBlank,
  CurrencyDollar,
  FileText,
  Warning,
  UserCircle,
  CaretLeft,
  Percent,
  ArrowRight,
  ArrowSquareOut,
} from 'phosphor-react';
import {
  useCompanies,
  ICompany,
  IEmpresaDetalhada,
  IFaturamentoImpostos,
  INotasQuebras,
  IFolhaPagamento,
} from '../../../hooks/useCompanies';
import { IDataNoPagination } from '../../../types/shared';
import { toCurrency, formatCNPJ, formatCPF, formatDate } from '../../../utils/FormatFunctions';

interface EmpresaDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  empresa: ICompany | null;
}

type ActiveView = 'dados' | 'faturamento' | 'notas' | 'folha';

const formatCPForCNPJ = (value: string | number | null | undefined): string => {
  if (!value) return '';
  const cleanValue = value.toString().replace(/\D/g, '');
  if (cleanValue.length === 14) {
    return formatCNPJ(value);
  }
  return formatCPF(value);
};

const formatCurrencyShort = (value: number): string => {
  if (value >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `R$ ${(value / 1000).toFixed(0)}K`;
  }
  return `R$ ${value.toFixed(0)}`;
};

export function EmpresaDetailModal({ isOpen, onClose, empresa }: EmpresaDetailModalProps) {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [activeView, setActiveView] = useState<ActiveView>('dados');

  const {
    getEmpresaDetalhada,
    getEmpresaFaturamentoImpostos,
    getEmpresaNotasQuebras,
    getEmpresaFolhaPagamento,
  } = useCompanies();

  const externalId = empresa?.external_id || '';

  const { data: empresaData, isFetching: isFetchingEmpresa } = useQuery<
    IDataNoPagination<IEmpresaDetalhada>
  >({
    queryKey: ['empresaDetalhada', { externalId }],
    queryFn: getEmpresaDetalhada,
    enabled: isOpen && !!externalId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: faturamentoData, isFetching: isFetchingFaturamento } = useQuery<
    IDataNoPagination<IFaturamentoImpostos>
  >({
    queryKey: ['empresaFaturamentoImpostos', { externalId, ano: selectedYear }],
    queryFn: getEmpresaFaturamentoImpostos,
    enabled: isOpen && !!externalId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: notasData, isFetching: isFetchingNotas } = useQuery<
    IDataNoPagination<INotasQuebras>
  >({
    queryKey: ['empresaNotasQuebras', { externalId, ano: selectedYear }],
    queryFn: getEmpresaNotasQuebras,
    enabled: isOpen && !!externalId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: folhaData, isFetching: isFetchingFolha } = useQuery<
    IDataNoPagination<IFolhaPagamento>
  >({
    queryKey: ['empresaFolhaPagamento', { externalId, ano: selectedYear }],
    queryFn: getEmpresaFolhaPagamento,
    enabled: isOpen && !!externalId,
    staleTime: 1000 * 60 * 5,
  });

  const empresaInfo = empresaData?.data;
  const faturamento = faturamentoData?.data;
  const notas = notasData?.data;
  const folha = folhaData?.data;

  const isLoading =
    isFetchingEmpresa || isFetchingFaturamento || isFetchingNotas || isFetchingFolha;

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Reset view when modal closes
  const handleClose = () => {
    setActiveView('dados');
    onClose();
  };

  if (!empresa) return null;

  // Preparar dados para gráficos
  const chartDataFaturamento =
    faturamento?.meses?.map(mes => ({
      mes: mes.mes_nome?.substring(0, 3) || '',
      faturamento: mes.faturamento.total,
      impostos:
        mes.impostos.pis +
        mes.impostos.cofins +
        mes.impostos.iss_normal +
        mes.impostos.iss_retido +
        mes.impostos.iss_total +
        mes.impostos.irpj +
        mes.impostos.csll,
    })) || [];

  const renderHeader = () => (
    <ModalHeader pb={2} bg="white" borderBottom="1px solid" borderColor="gray.100">
      <Flex align="center" justify="space-between" pr={8}>
        <Flex align="center" gap={3}>
          {activeView !== 'dados' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActiveView('dados')}
              leftIcon={<Icon as={CaretLeft} />}
              color="gray.600"
              _hover={{ bg: 'gray.100' }}
            >
              Voltar
            </Button>
          )}
          <Avatar
            size="sm"
            bg="gray.700"
            icon={<Icon as={Buildings} boxSize={4} color="white" />}
          />
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1}>
              {empresaInfo?.razao_social || empresa.razao_social || empresa.nome}
            </Text>
            <Text fontSize="xs" color="gray.400">
              {formatCNPJ(empresaInfo?.cnpj || empresa.cnpj)}
            </Text>
          </Box>
        </Flex>
        <HStack spacing={2}>
          <Select
            size="xs"
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
            w="70px"
            bg="gray.50"
            border="none"
            fontSize="xs"
          >
            {years.map(year => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </HStack>
      </Flex>
    </ModalHeader>
  );

  // Tela de Dados da Empresa com Dashboard
  const renderDadosView = () => (
    <VStack spacing={4} align="stretch">
      {/* Menu Cards - No topo */}
      <SimpleGrid columns={3} spacing={3}>
        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          cursor="pointer"
          transition="all 0.15s"
          _hover={{ borderColor: 'blue.300', boxShadow: 'sm' }}
          onClick={() => setActiveView('faturamento')}
        >
          <Flex align="center" justify="space-between">
            <HStack spacing={2}>
              <Box p={2} bg="blue.50" borderRadius="md">
                <Icon as={CurrencyDollar} boxSize={4} color="blue.500" />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.700">
                  Faturamento
                </Text>
                <Text fontSize="2xs" color="gray.400">
                  Receitas e impostos
                </Text>
              </Box>
            </HStack>
            <Icon as={ArrowRight} boxSize={4} color="gray.400" />
          </Flex>
        </Box>

        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          cursor="pointer"
          transition="all 0.15s"
          _hover={{ borderColor: 'green.300', boxShadow: 'sm' }}
          onClick={() => setActiveView('notas')}
        >
          <Flex align="center" justify="space-between">
            <HStack spacing={2}>
              <Box p={2} bg="green.50" borderRadius="md">
                <Icon as={FileText} boxSize={4} color="green.500" />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.700">
                  Notas Fiscais
                </Text>
                <Text fontSize="2xs" color="gray.400">
                  Emissão e quebras
                </Text>
              </Box>
            </HStack>
            <Icon as={ArrowRight} boxSize={4} color="gray.400" />
          </Flex>
        </Box>

        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          cursor="pointer"
          transition="all 0.15s"
          _hover={{ borderColor: 'purple.300', boxShadow: 'sm' }}
          onClick={() => setActiveView('folha')}
        >
          <Flex align="center" justify="space-between">
            <HStack spacing={2}>
              <Box p={2} bg="purple.50" borderRadius="md">
                <Icon as={UserCircle} boxSize={4} color="purple.500" />
              </Box>
              <Box>
                <Text fontSize="xs" fontWeight="medium" color="gray.700">
                  Folha de Pagamento
                </Text>
                <Text fontSize="2xs" color="gray.400">
                  FGTS e DCTF-Web
                </Text>
              </Box>
            </HStack>
            <Icon as={ArrowRight} boxSize={4} color="gray.400" />
          </Flex>
        </Box>
      </SimpleGrid>

      {/* Info Cards Row */}
      <SimpleGrid columns={4} spacing={3}>
        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          position="relative"
        >
          <Text fontSize="2xs" color="gray.400" textTransform="uppercase" letterSpacing="wider">
            Faturamento {selectedYear}
          </Text>
          <Text fontSize="md" fontWeight="bold" color="gray.800" mt={1}>
            {toCurrency(faturamento?.totais?.faturamento?.total || 0)}
          </Text>
          <ChakraTooltip label="Ver faturamento" fontSize="xs" hasArrow>
            <IconButton
              aria-label="Ver faturamento"
              icon={<Icon as={ArrowSquareOut} boxSize={3.5} />}
              size="xs"
              variant="ghost"
              colorScheme="gray"
              position="absolute"
              bottom={1}
              right={1}
              onClick={() => {
                const nowMonth = new Date().getMonth() + 1;
                const mes = selectedYear < currentYear ? 12 : nowMonth;
                const lastDay = new Date(selectedYear, mes, 0).getDate();
                const dateStart = `${selectedYear}-${String(mes).padStart(2, '0')}-01`;
                const dateEnd = `${selectedYear}-${String(mes).padStart(2, '0')}-${lastDay}`;
                window.open(
                  `/modulesHome/integration/billingAnalysis?page=1&size=50&date_start=${dateStart}&date_end=${dateEnd}&companyId=&screen_data=faturamento&external_id=${externalId}`,
                  '_blank',
                );
              }}
            />
          </ChakraTooltip>
        </Box>
        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          position="relative"
        >
          <Text fontSize="2xs" color="gray.400" textTransform="uppercase" letterSpacing="wider">
            Notas Emitidas
          </Text>
          <Text fontSize="lg" fontWeight="bold" color="gray.800" mt={1}>
            {notas?.totais?.notas?.total || 0}
          </Text>
          <ChakraTooltip label="Ver notas" fontSize="xs" hasArrow>
            <IconButton
              aria-label="Ver notas"
              icon={<Icon as={ArrowSquareOut} boxSize={3.5} />}
              size="xs"
              variant="ghost"
              colorScheme="gray"
              position="absolute"
              bottom={1}
              right={1}
              onClick={() => {
                const nowMonth = new Date().getMonth() + 1;
                const mes = selectedYear < currentYear ? 12 : nowMonth;
                const lastDay = new Date(selectedYear, mes, 0).getDate();
                const dateStart = `${selectedYear}-${String(mes).padStart(2, '0')}-01`;
                const dateEnd = `${selectedYear}-${String(mes).padStart(2, '0')}-${lastDay}`;
                window.open(
                  `/modulesHome/integration/apixsieg?page=1&size=50&date_start=${dateStart}&date_end=${dateEnd}&screen_data=api_sieg&external_id=${externalId}`,
                  '_blank',
                );
              }}
            />
          </ChakraTooltip>
        </Box>
        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          position="relative"
        >
          <Text fontSize="2xs" color="gray.400" textTransform="uppercase" letterSpacing="wider">
            Funcionários
          </Text>
          <Text fontSize="lg" fontWeight="bold" color="gray.800" mt={1}>
            {folha?.qnt_vidas || 0}
          </Text>
          <ChakraTooltip label="Ver no Indicadores" fontSize="xs" hasArrow>
            <IconButton
              aria-label="Ver no Indicadores"
              icon={<Icon as={ArrowSquareOut} boxSize={3.5} />}
              size="xs"
              variant="ghost"
              colorScheme="gray"
              position="absolute"
              bottom={1}
              right={1}
              onClick={() => {
                const nowMonth = new Date().getMonth() + 1;
                const mes = selectedYear < currentYear ? 12 : nowMonth;
                const lastDay = new Date(selectedYear, mes, 0).getDate();
                const dateStart = `${selectedYear}-${String(mes).padStart(2, '0')}-01`;
                const dateEnd = `${selectedYear}-${String(mes).padStart(2, '0')}-${lastDay}`;
                window.open(
                  `https://indicadores.redeespecialistas.com.br/modulesHome/personnelDepartment/payRoll?page=1&size=50&date_start=${dateStart}&date_end=${dateEnd}&screen_data=fgts_folha&external_id=${externalId}`,
                  '_blank',
                );
              }}
            />
          </ChakraTooltip>
        </Box>
        <Box
          bg={(notas?.totais?.quebras?.total ?? 0) > 0 ? 'red.50' : 'white'}
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor={(notas?.totais?.quebras?.total ?? 0) > 0 ? 'red.200' : 'gray.100'}
          position="relative"
        >
          <Text fontSize="2xs" color="gray.400" textTransform="uppercase" letterSpacing="wider">
            Quebras
          </Text>
          <Text
            fontSize="lg"
            fontWeight="bold"
            color={(notas?.totais?.quebras?.total ?? 0) > 0 ? 'red.600' : 'gray.400'}
            mt={1}
          >
            {notas?.totais?.quebras?.total || 0}
          </Text>
          {(notas?.totais?.quebras?.total ?? 0) > 0 && (
            <ChakraTooltip label="Ver quebras" fontSize="xs" hasArrow>
              <IconButton
                aria-label="Ver quebras"
                icon={<Icon as={ArrowSquareOut} boxSize={3.5} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                position="absolute"
                bottom={1}
                right={1}
                onClick={() => {
                  // Encontra o primeiro mês com quebras
                  const mesComQuebra = notas?.meses?.find(m => m.quebras.total > 0);
                  const mes = mesComQuebra?.mes || 1;
                  const lastDay = new Date(selectedYear, mes, 0).getDate();
                  const dateStart = `${selectedYear}-${String(mes).padStart(2, '0')}-01`;
                  const dateEnd = `${selectedYear}-${String(mes).padStart(2, '0')}-${lastDay}`;
                  window.open(
                    `/breaks/${externalId}?date_start=${dateStart}&date_end=${dateEnd}`,
                    '_blank',
                  );
                }}
              />
            </ChakraTooltip>
          )}
        </Box>
      </SimpleGrid>

      {/* Company Info & Partners */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {/* Informações da Empresa */}
        <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.100">
          <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={3}>
            Dados Cadastrais
          </Text>
          <VStack align="stretch" spacing={2}>
            <Flex justify="space-between" py={1} borderBottom="1px solid" borderColor="gray.50">
              <Text fontSize="xs" color="gray.500">
                Razão Social
              </Text>
              <Text
                fontSize="xs"
                fontWeight="medium"
                color="gray.700"
                maxW="60%"
                textAlign="right"
                noOfLines={1}
              >
                {empresaInfo?.razao_social || empresa.razao_social || '-'}
              </Text>
            </Flex>
            <Flex justify="space-between" py={1} borderBottom="1px solid" borderColor="gray.50">
              <Text fontSize="xs" color="gray.500">
                Nome Fantasia
              </Text>
              <Text
                fontSize="xs"
                fontWeight="medium"
                color="gray.700"
                maxW="60%"
                textAlign="right"
                noOfLines={1}
              >
                {empresaInfo?.nome || empresa.nome || '-'}
              </Text>
            </Flex>
            <Flex justify="space-between" py={1} borderBottom="1px solid" borderColor="gray.50">
              <Text fontSize="xs" color="gray.500">
                CNPJ
              </Text>
              <Text fontSize="xs" fontWeight="medium" color="gray.700">
                {formatCNPJ(empresaInfo?.cnpj || empresa.cnpj)}
              </Text>
            </Flex>
            <Flex justify="space-between" py={1} borderBottom="1px solid" borderColor="gray.50">
              <Text fontSize="xs" color="gray.500">
                Regime Tributário
              </Text>
              <Badge colorScheme="purple" fontSize="2xs" variant="subtle">
                {empresaInfo?.regime_tributario_atualizado || '-'}
              </Badge>
            </Flex>
            <Flex justify="space-between" py={1} borderBottom="1px solid" borderColor="gray.50">
              <Text fontSize="xs" color="gray.500">
                Localização
              </Text>
              <HStack spacing={1}>
                <Icon as={MapPin} color="gray.400" boxSize={3} />
                <Text fontSize="xs" fontWeight="medium" color="gray.700">
                  {empresaInfo?.cidade || '-'} - {empresaInfo?.uf || '-'}
                </Text>
              </HStack>
            </Flex>
            <Flex justify="space-between" py={1}>
              <Text fontSize="xs" color="gray.500">
                Cliente Desde
              </Text>
              <HStack spacing={1}>
                <Icon as={CalendarBlank} color="gray.400" boxSize={3} />
                <Text fontSize="xs" fontWeight="medium" color="gray.700">
                  {empresaInfo?.dt_cli_desde ? formatDate(empresaInfo.dt_cli_desde) : '-'}
                </Text>
              </HStack>
            </Flex>
          </VStack>
        </Box>

        {/* Quadro Societário */}
        <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.100">
          <Flex justify="space-between" align="center" mb={3}>
            <HStack>
              <Icon as={Users} color="gray.500" boxSize={4} />
              <Text fontSize="xs" fontWeight="semibold" color="gray.600">
                Quadro Societário
              </Text>
              {empresaInfo?.socios && (
                <Badge colorScheme="gray" fontSize="2xs" variant="subtle">
                  {empresaInfo.socios.length}
                </Badge>
              )}
            </HStack>
            <ChakraTooltip label="Ver análise de sócios" fontSize="xs" hasArrow>
              <IconButton
                aria-label="Ver análise de sócios"
                icon={<Icon as={ArrowSquareOut} boxSize={3.5} />}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                as="a"
                href={`/empresas/analise-socios?page=1&size=50&status_filter=todas&external_id=${externalId}`}
                target="_blank"
              />
            </ChakraTooltip>
          </Flex>
          <VStack align="stretch" spacing={2} maxH="180px" overflowY="auto">
            {empresaInfo?.socios?.map(socio => (
              <Flex
                key={socio.inscricao}
                p={2}
                bg="gray.50"
                borderRadius="md"
                justify="space-between"
                align="center"
              >
                <Box flex={1}>
                  <Text fontSize="xs" fontWeight="medium" color="gray.700" noOfLines={1}>
                    {socio.nome_socio}
                  </Text>
                  <Text fontSize="2xs" color="gray.400">
                    {formatCPForCNPJ(socio.inscricao)}
                  </Text>
                </Box>
                <VStack align="end" spacing={0}>
                  <Badge colorScheme="blue" fontSize="xs" variant="subtle">
                    <Flex align="center" gap={1}>
                      <Icon as={Percent} boxSize={2} />
                      {socio.participacao.toFixed(1)}
                    </Flex>
                  </Badge>
                  <Text fontSize="2xs" color="gray.400">
                    {socio.qt_cotas?.toLocaleString('pt-BR')} cotas
                  </Text>
                </VStack>
              </Flex>
            )) || (
              <Text fontSize="xs" color="gray.400" textAlign="center" py={4}>
                Sem sócios cadastrados
              </Text>
            )}
          </VStack>
        </Box>
      </SimpleGrid>

      {/* Charts Row */}
      <SimpleGrid columns={2} spacing={4}>
        {/* Gráfico de Faturamento */}
        <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.100">
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600">
              Evolução do Faturamento
            </Text>
            <Button
              size="xs"
              variant="ghost"
              rightIcon={<Icon as={ArrowRight} boxSize={3} />}
              color="blue.500"
              onClick={() => setActiveView('faturamento')}
              _hover={{ bg: 'blue.50' }}
            >
              Detalhes
            </Button>
          </Flex>
          <Box h="160px">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartDataFaturamento}
                margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorFat" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3182CE" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3182CE" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 10 }} stroke="#a0aec0" />
                <YAxis
                  tick={{ fontSize: 10 }}
                  stroke="#a0aec0"
                  tickFormatter={formatCurrencyShort}
                />
                <Tooltip
                  formatter={(value: number) => [toCurrency(value), 'Faturamento']}
                  contentStyle={{ fontSize: 12 }}
                />
                <Area
                  type="monotone"
                  dataKey="faturamento"
                  stroke="#3182CE"
                  strokeWidth={2}
                  fill="url(#colorFat)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* Gráfico de Notas */}
        <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.100">
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600">
              Evolução Notas Fiscais
            </Text>
            <Button
              size="xs"
              variant="ghost"
              rightIcon={<Icon as={ArrowRight} boxSize={3} />}
              color="green.500"
              onClick={() => setActiveView('notas')}
              _hover={{ bg: 'green.50' }}
            >
              Detalhes
            </Button>
          </Flex>
          <Box h="160px">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={
                  notas?.meses?.map(mes => ({
                    mes: mes.mes_nome?.substring(0, 3) || '',
                    nfe: mes.notas.nfe,
                    nfce: mes.notas.nfce,
                    nfse: mes.notas.nfse,
                    cte: mes.notas.cte,
                  })) || []
                }
                margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorNfe" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3182CE" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3182CE" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorNfce" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38A169" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#38A169" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorNfse" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#805AD5" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#805AD5" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorCte" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#DD6B20" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#DD6B20" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 10 }} stroke="#a0aec0" />
                <YAxis tick={{ fontSize: 10 }} stroke="#a0aec0" />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Area
                  type="monotone"
                  dataKey="nfe"
                  name="NFe"
                  stroke="#3182CE"
                  strokeWidth={2}
                  fill="url(#colorNfe)"
                />
                <Area
                  type="monotone"
                  dataKey="nfce"
                  name="NFCe"
                  stroke="#38A169"
                  strokeWidth={2}
                  fill="url(#colorNfce)"
                />
                <Area
                  type="monotone"
                  dataKey="nfse"
                  name="NFSe"
                  stroke="#805AD5"
                  strokeWidth={2}
                  fill="url(#colorNfse)"
                />
                <Area
                  type="monotone"
                  dataKey="cte"
                  name="CTe"
                  stroke="#DD6B20"
                  strokeWidth={2}
                  fill="url(#colorCte)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </SimpleGrid>

      {/* Gráficos de Impostos e Folha */}
      <SimpleGrid columns={2} spacing={4}>
        {/* Gráfico de Impostos por Mês */}
        <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.100">
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600">
              Evolução dos Impostos
            </Text>
            <Button
              size="xs"
              variant="ghost"
              rightIcon={<Icon as={ArrowRight} boxSize={3} />}
              color="orange.500"
              onClick={() => setActiveView('faturamento')}
              _hover={{ bg: 'orange.50' }}
            >
              Detalhes
            </Button>
          </Flex>
          <Box h="160px">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={
                  faturamento?.meses?.map(mes => ({
                    mes: mes.mes_nome?.substring(0, 3) || '',
                    pis: mes.impostos.pis,
                    cofins: mes.impostos.cofins,
                    irpj: mes.impostos.irpj,
                    csll: mes.impostos.csll,
                    icms: mes.impostos.icms,
                    iss:
                      mes.impostos.iss_total || mes.impostos.iss_normal + mes.impostos.iss_retido,
                  })) || []
                }
                margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorPis" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorCofins" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorIrpj" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff8042" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ff8042" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorCsll" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ffc658" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ffc658" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorIcms" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0088fe" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0088fe" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorIss" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00C49F" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#00C49F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 10 }} stroke="#a0aec0" />
                <YAxis
                  tick={{ fontSize: 10 }}
                  stroke="#a0aec0"
                  tickFormatter={formatCurrencyShort}
                />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    const labels: Record<string, string> = {
                      pis: 'PIS',
                      cofins: 'COFINS',
                      irpj: 'IRPJ',
                      csll: 'CSLL',
                      icms: 'ICMS',
                      iss: 'ISS',
                    };
                    return [toCurrency(value), labels[name] || name];
                  }}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 9 }} />
                <Area
                  type="monotone"
                  dataKey="pis"
                  name="PIS"
                  stroke="#8884d8"
                  strokeWidth={2}
                  fill="url(#colorPis)"
                />
                <Area
                  type="monotone"
                  dataKey="cofins"
                  name="COFINS"
                  stroke="#82ca9d"
                  strokeWidth={2}
                  fill="url(#colorCofins)"
                />
                <Area
                  type="monotone"
                  dataKey="irpj"
                  name="IRPJ"
                  stroke="#ff8042"
                  strokeWidth={2}
                  fill="url(#colorIrpj)"
                />
                <Area
                  type="monotone"
                  dataKey="csll"
                  name="CSLL"
                  stroke="#ffc658"
                  strokeWidth={2}
                  fill="url(#colorCsll)"
                />
                <Area
                  type="monotone"
                  dataKey="icms"
                  name="ICMS"
                  stroke="#0088fe"
                  strokeWidth={2}
                  fill="url(#colorIcms)"
                />
                <Area
                  type="monotone"
                  dataKey="iss"
                  name="ISS"
                  stroke="#00C49F"
                  strokeWidth={2}
                  fill="url(#colorIss)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* Gráfico de Folha de Pagamento (FGTS e DCTF-Web) */}
        <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.100">
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontSize="xs" fontWeight="semibold" color="gray.600">
              Evolução FGTS e DCTF-Web
            </Text>
            <Button
              size="xs"
              variant="ghost"
              rightIcon={<Icon as={ArrowRight} boxSize={3} />}
              color="purple.500"
              onClick={() => setActiveView('folha')}
              _hover={{ bg: 'purple.50' }}
            >
              Detalhes
            </Button>
          </Flex>
          <Box h="160px">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={
                  folha?.meses?.map(mes => ({
                    mes: mes.mes_nome?.substring(0, 3) || '',
                    fgts: mes.fgts,
                    dctfweb: mes.dctfweb,
                  })) || []
                }
                margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="colorFgts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38A169" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#38A169" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorDctf" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#805AD5" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#805AD5" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 10 }} stroke="#a0aec0" />
                <YAxis
                  tick={{ fontSize: 10 }}
                  stroke="#a0aec0"
                  tickFormatter={formatCurrencyShort}
                />
                <Tooltip
                  formatter={(value: number, name: string) => {
                    const labels: Record<string, string> = { fgts: 'FGTS', dctfweb: 'DCTF-Web' };
                    return [toCurrency(value), labels[name] || name];
                  }}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 10 }}
                  formatter={value => {
                    const labels: Record<string, string> = { fgts: 'FGTS', dctfweb: 'DCTF-Web' };
                    return labels[value] || value;
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="fgts"
                  stroke="#38A169"
                  strokeWidth={2}
                  fill="url(#colorFgts)"
                />
                <Area
                  type="monotone"
                  dataKey="dctfweb"
                  stroke="#805AD5"
                  strokeWidth={2}
                  fill="url(#colorDctf)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </SimpleGrid>
    </VStack>
  );

  // Tela de Faturamento
  const renderFaturamentoView = () => (
    <VStack spacing={4} align="stretch">
      {/* Resumo Faturamento */}
      <SimpleGrid columns={4} spacing={3}>
        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          textAlign="center"
        >
          <Text fontSize="2xs" color="gray.400" textTransform="uppercase">
            Serviço
          </Text>
          <Text fontSize="md" fontWeight="bold" color="blue.600">
            {toCurrency(faturamento?.totais?.faturamento?.servico || 0)}
          </Text>
        </Box>
        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          textAlign="center"
        >
          <Text fontSize="2xs" color="gray.400" textTransform="uppercase">
            Saída
          </Text>
          <Text fontSize="md" fontWeight="bold" color="green.600">
            {toCurrency(faturamento?.totais?.faturamento?.saida || 0)}
          </Text>
        </Box>
        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          textAlign="center"
        >
          <Text fontSize="2xs" color="gray.400" textTransform="uppercase">
            Outros
          </Text>
          <Text fontSize="md" fontWeight="bold" color="purple.600">
            {toCurrency(faturamento?.totais?.faturamento?.outros || 0)}
          </Text>
        </Box>
        <Box
          bg="blue.50"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="blue.200"
          textAlign="center"
        >
          <Text fontSize="2xs" color="blue.600" textTransform="uppercase" fontWeight="medium">
            Total
          </Text>
          <Text fontSize="md" fontWeight="bold" color="blue.700">
            {toCurrency(faturamento?.totais?.faturamento?.total || 0)}
          </Text>
        </Box>
      </SimpleGrid>

      {/* Impostos Totais - No topo */}
      <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.100">
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={3}>
          Impostos Totais - {selectedYear}
        </Text>
        <Flex
          w="full"
          direction="row"
          wrap="wrap"
          justify="center"
          align="stretch"
          gap={{ base: '3rem', md: '2rem', lg: '2rem' }}
        >
          {[
            { label: 'PIS', value: faturamento?.totais?.impostos?.pis },
            { label: 'COFINS', value: faturamento?.totais?.impostos?.cofins },
            { label: 'ISS RETIDO', value: faturamento?.totais?.impostos?.iss_retido },
            { label: 'ISS NORMAL', value: faturamento?.totais?.impostos?.iss_normal },
            { label: 'IRPJ', value: faturamento?.totais?.impostos?.irpj },
            { label: 'CSLL', value: faturamento?.totais?.impostos?.csll },
            { label: 'ICMS', value: faturamento?.totais?.impostos?.icms },
          ].map(item => (
            <Box key={item.label} textAlign="center" p={2} bg="gray.50" borderRadius="md">
              <Text fontSize="2xs" color="gray.500">
                {item.label}
              </Text>
              <Text fontSize="xs" fontWeight="bold">
                {toCurrency(item.value || 0)}
              </Text>
            </Box>
          ))}
        </Flex>
      </Box>

      {/* Tabela Mensal */}
      <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.100">
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={3}>
          Faturamento Mensal - {selectedYear}
        </Text>
        <Box overflowX="auto" maxW="100%">
          <Table size="sm" variant="simple" minW="900px">
            <Thead>
              {/* mes, servico, saida, outros,total, pis, cofins,iss, csllicsm.irpj  */}
              <Tr bg="gray.50">
                <Th fontSize="2xs" color="gray.500" px={2}>
                  Mês
                </Th>
                <Th fontSize="2xs" color="gray.500" isNumeric px={2}>
                  Serviço
                </Th>
                <Th fontSize="2xs" color="gray.500" isNumeric px={2}>
                  Saída
                </Th>
                <Th fontSize="2xs" color="gray.500" isNumeric px={2}>
                  Outros
                </Th>
                <Th fontSize="2xs" color="gray.500" isNumeric px={2} bg="blue.50">
                  Total
                </Th>
                <Th fontSize="2xs" color="gray.500" isNumeric px={2}>
                  PIS
                </Th>
                <Th fontSize="2xs" color="gray.500" isNumeric px={2}>
                  COFINS
                </Th>
                <Th fontSize="2xs" color="gray.500" isNumeric px={2}>
                  ISS RETIDO
                </Th>
                <Th fontSize="2xs" color="gray.500" isNumeric px={2}>
                  ISS NORMAL
                </Th>
                <Th fontSize="2xs" color="gray.500" isNumeric px={2}>
                  CSLL
                </Th>
                <Th fontSize="2xs" color="gray.500" isNumeric px={2}>
                  ICMS
                </Th>
                <Th fontSize="2xs" color="gray.500" isNumeric px={2}>
                  IRPJ
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {/* mes, servico, saida, outros,total, pis, cofins,iss, csll,icsm.irpj  */}
              {faturamento?.meses?.map(mes => {
                return (
                  // mes
                  <Tr key={mes.mes} _hover={{ bg: 'gray.50' }}>
                    <Td fontSize="xs" fontWeight="medium" px={2}>
                      {mes.mes_nome?.substring(0, 3)}
                    </Td>
                    {/* servico */}
                    <Td fontSize="xs" isNumeric px={2}>
                      {toCurrency(mes.faturamento.servico)}
                    </Td>
                    {/* saida */}
                    <Td fontSize="xs" isNumeric px={2}>
                      {toCurrency(mes.faturamento.saida)}
                    </Td>
                    {/* outro */}
                    <Td fontSize="xs" isNumeric px={2}>
                      {toCurrency(mes.faturamento.outros)}
                    </Td>
                    {/* total */}
                    <Td fontSize="xs" isNumeric color="gray.600" px={2}>
                      {toCurrency(mes.faturamento.total)}
                    </Td>
                    {/* pis */}
                    <Td fontSize="xs" isNumeric color="gray.600" px={2}>
                      {toCurrency(mes.impostos.pis)}
                    </Td>
                    {/*  cofins */}
                    <Td fontSize="xs" isNumeric color="gray.600" px={2}>
                      {toCurrency(mes.impostos.cofins)}
                    </Td>
                    {/* iss */}
                    <Td fontSize="xs" isNumeric color="gray.600" px={2}>
                      {toCurrency(mes.impostos.iss_retido)}
                    </Td>
                    <Td fontSize="xs" isNumeric color="gray.600" px={2}>
                      {toCurrency(mes.impostos.iss_normal)}
                    </Td>
                    {/* csll */}
                    <Td fontSize="xs" isNumeric color="gray.600" px={2}>
                      {toCurrency(mes.impostos.csll)}
                    </Td>
                    {/* icms */}
                    <Td fontSize="xs" isNumeric color="gray.600" px={2}>
                      {toCurrency(mes.impostos.icms)}
                    </Td>
                    {/* irpj */}
                    <Td fontSize="xs" isNumeric color="gray.600" px={2}>
                      {toCurrency(mes.impostos.irpj)}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </VStack>
  );

  // Tela de Notas Fiscais
  const renderNotasView = () => (
    <VStack spacing={4} align="stretch">
      {/* Resumo por tipo */}
      <SimpleGrid columns={5} spacing={3}>
        {[
          { label: 'NFe', value: notas?.totais?.notas?.nfe, color: 'blue' },
          { label: 'NFCe', value: notas?.totais?.notas?.nfce, color: 'green' },
          { label: 'NFSe', value: notas?.totais?.notas?.nfse, color: 'purple' },
          { label: 'CTe', value: notas?.totais?.notas?.cte, color: 'orange' },
        ].map(item => (
          <Box
            key={item.label}
            bg="white"
            p={3}
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.100"
            textAlign="center"
          >
            <Text fontSize="2xs" color="gray.400" textTransform="uppercase">
              {item.label}
            </Text>
            <Text fontSize="xl" fontWeight="bold" color={`${item.color}.600`}>
              {item.value || 0}
            </Text>
          </Box>
        ))}
        <Box
          bg={(notas?.totais?.quebras?.total ?? 0) > 0 ? 'red.50' : 'white'}
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor={(notas?.totais?.quebras?.total ?? 0) > 0 ? 'red.200' : 'gray.100'}
          textAlign="center"
          position="relative"
        >
          <Text fontSize="2xs" color="gray.400" textTransform="uppercase">
            Quebras
          </Text>
          <Text
            fontSize="xl"
            fontWeight="bold"
            color={(notas?.totais?.quebras?.total ?? 0) > 0 ? 'red.600' : 'gray.400'}
          >
            {notas?.totais?.quebras?.total || 0}
          </Text>
          {(notas?.totais?.quebras?.total ?? 0) > 0 && (
            <ChakraTooltip label="Ver quebras" fontSize="xs" hasArrow>
              <IconButton
                aria-label="Ver quebras"
                icon={<Icon as={ArrowSquareOut} boxSize={3.5} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                position="absolute"
                bottom={1}
                right={1}
                onClick={() => {
                  const mesComQuebra = notas?.meses?.find(m => m.quebras.total > 0);
                  const mes = mesComQuebra?.mes || 1;
                  const lastDay = new Date(selectedYear, mes, 0).getDate();
                  const dateStart = `${selectedYear}-${String(mes).padStart(2, '0')}-01`;
                  const dateEnd = `${selectedYear}-${String(mes).padStart(2, '0')}-${lastDay}`;
                  window.open(
                    `/breaks/${externalId}?date_start=${dateStart}&date_end=${dateEnd}`,
                    '_blank',
                  );
                }}
              />
            </ChakraTooltip>
          )}
        </Box>
      </SimpleGrid>

      {/* Alerta de quebras */}
      {(notas?.totais?.quebras?.total ?? 0) > 0 && (
        <Box bg="red.50" p={3} borderRadius="lg" border="1px solid" borderColor="red.200">
          <Flex align="center" gap={2}>
            <Icon as={Warning} boxSize={4} color="red.500" />
            <Box>
              <Text fontWeight="medium" color="red.700" fontSize="xs">
                Quebras de sequência encontradas
              </Text>
              <Text fontSize="2xs" color="red.600">
                {notas?.totais?.quebras?.total} quebras em {selectedYear}
              </Text>
            </Box>
          </Flex>
        </Box>
      )}

      {/* Tabela Mensal */}
      <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.100">
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={3}>
          Notas por Mês - {selectedYear}
        </Text>
        <Box overflowX="auto">
          <Table size="md" variant="simple">
            <Thead>
              <Tr bg="gray.50">
                <Th fontSize="xs" color="gray.500">
                  Mês
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  NFe
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  NFCe
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  NFSe
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  CTe
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  Total
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  Quebras
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {notas?.meses?.map(mes => (
                <Tr key={mes.mes} _hover={{ bg: 'gray.50' }}>
                  <Td fontSize="sm" fontWeight="medium">
                    {mes.mes_nome}
                  </Td>
                  <Td fontSize="sm" isNumeric>
                    {mes.notas.nfe}
                  </Td>
                  <Td fontSize="sm" isNumeric>
                    {mes.notas.nfce}
                  </Td>
                  <Td fontSize="sm" isNumeric>
                    {mes.notas.nfse}
                  </Td>
                  <Td fontSize="sm" isNumeric>
                    {mes.notas.cte}
                  </Td>
                  <Td fontSize="sm" isNumeric fontWeight="bold" color="green.600">
                    {mes.notas.total}
                  </Td>
                  <Td fontSize="sm" isNumeric>
                    {mes.quebras.total > 0 ? (
                      <Badge colorScheme="red" fontSize="xs">
                        {mes.quebras.total}
                      </Badge>
                    ) : (
                      <Text color="gray.400">0</Text>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </VStack>
  );

  // Tela de Folha de Pagamento
  const renderFolhaView = () => (
    <VStack spacing={4} align="stretch">
      {/* Resumo */}
      <SimpleGrid columns={4} spacing={3}>
        <Box
          bg="purple.50"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="purple.200"
          textAlign="center"
          position="relative"
        >
          <Text fontSize="2xs" color="purple.600" textTransform="uppercase" fontWeight="medium">
            Funcionários
          </Text>
          <Text fontSize="xl" fontWeight="bold" color="purple.700">
            {folha?.qnt_vidas || 0}
          </Text>
          <ChakraTooltip label="Ver no Indicadores" fontSize="xs" hasArrow>
            <IconButton
              aria-label="Ver no Indicadores"
              icon={<Icon as={ArrowSquareOut} boxSize={3.5} />}
              size="xs"
              variant="ghost"
              colorScheme="purple"
              position="absolute"
              bottom={1}
              right={1}
              onClick={() => {
                const nowMonth = new Date().getMonth() + 1;
                const mes = selectedYear < currentYear ? 12 : nowMonth;
                const lastDay = new Date(selectedYear, mes, 0).getDate();
                const dateStart = `${selectedYear}-${String(mes).padStart(2, '0')}-01`;
                const dateEnd = `${selectedYear}-${String(mes).padStart(2, '0')}-${lastDay}`;
                window.open(
                  `https://indicadores.redeespecialistas.com.br/modulesHome/personnelDepartment/payRoll?page=1&size=50&date_start=${dateStart}&date_end=${dateEnd}&screen_data=fgts_folha&external_id=${externalId}`,
                  '_blank',
                );
              }}
            />
          </ChakraTooltip>
        </Box>
        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          textAlign="center"
        >
          <Text fontSize="2xs" color="gray.400" textTransform="uppercase">
            Pró-labores
          </Text>
          <Text fontSize="xl" fontWeight="bold" color="blue.600">
            {folha?.qnt_prolabore || 0}
          </Text>
        </Box>
        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          textAlign="center"
        >
          <Text fontSize="2xs" color="gray.400" textTransform="uppercase">
            Total FGTS
          </Text>
          <Text fontSize="md" fontWeight="bold" color="green.600">
            {toCurrency(folha?.totais?.fgts || 0)}
          </Text>
        </Box>
        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.100"
          textAlign="center"
        >
          <Text fontSize="2xs" color="gray.400" textTransform="uppercase">
            Total DCTF-Web
          </Text>
          <Text fontSize="md" fontWeight="bold" color="orange.600">
            {toCurrency(folha?.totais?.dctfweb || 0)}
          </Text>
        </Box>
      </SimpleGrid>

      {/* Movimentações */}
      <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.100">
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={3}>
          Movimentações - {selectedYear}
        </Text>
        <SimpleGrid columns={4} spacing={3}>
          {[
            { label: 'Admissões', value: folha?.totais?.admissoes, color: 'teal' },
            { label: 'Demissões', value: folha?.totais?.demissoes, color: 'red' },
            { label: 'Férias', value: folha?.totais?.ferias, color: 'yellow' },
            { label: 'Afastamentos', value: folha?.totais?.afastamentos, color: 'gray' },
          ].map(item => (
            <Box
              key={item.label}
              textAlign="center"
              p={3}
              bg={`${item.color}.50`}
              borderRadius="lg"
            >
              <Text fontSize="2xl" fontWeight="bold" color={`${item.color}.600`}>
                {item.value || 0}
              </Text>
              <Text fontSize="2xs" color={`${item.color}.700`}>
                {item.label}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* Tabela Mensal */}
      <Box bg="white" p={4} borderRadius="lg" border="1px solid" borderColor="gray.100">
        <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={3}>
          Folha Mensal - {selectedYear}
        </Text>
        <Box overflowX="auto">
          <Table size="md" variant="simple">
            <Thead>
              <Tr bg="gray.50">
                <Th fontSize="xs" color="gray.500">
                  Mês
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  Admissões
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  Demissões
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  Férias
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  Afastamentos
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  FGTS
                </Th>
                <Th fontSize="xs" color="gray.500" isNumeric>
                  DCTF-Web
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {folha?.meses?.map(mes => (
                <Tr key={mes.mes} _hover={{ bg: 'gray.50' }}>
                  <Td fontSize="sm" fontWeight="medium">
                    {mes.mes_nome}
                  </Td>
                  <Td fontSize="sm" isNumeric>
                    {mes.admissoes > 0 ? (
                      <Badge colorScheme="teal" fontSize="xs">
                        {mes.admissoes}
                      </Badge>
                    ) : (
                      <Text color="gray.400">0</Text>
                    )}
                  </Td>
                  <Td fontSize="sm" isNumeric>
                    {mes.demissoes > 0 ? (
                      <Badge colorScheme="red" fontSize="xs">
                        {mes.demissoes}
                      </Badge>
                    ) : (
                      <Text color="gray.400">0</Text>
                    )}
                  </Td>
                  <Td fontSize="sm" isNumeric>
                    {mes.ferias > 0 ? (
                      <Badge colorScheme="yellow" fontSize="xs">
                        {mes.ferias}
                      </Badge>
                    ) : (
                      <Text color="gray.400">0</Text>
                    )}
                  </Td>
                  <Td fontSize="sm" isNumeric>
                    {mes.afastamentos}
                  </Td>
                  <Td fontSize="sm" isNumeric>
                    {toCurrency(mes.fgts)}
                  </Td>
                  <Td fontSize="sm" isNumeric>
                    {toCurrency(mes.dctfweb)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>
    </VStack>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex justify="center" align="center" h="300px">
          <Spinner size="lg" color="gray.400" thickness="3px" />
        </Flex>
      );
    }

    switch (activeView) {
      case 'faturamento':
        return renderFaturamentoView();
      case 'notas':
        return renderNotasView();
      case 'folha':
        return renderFolhaView();
      default:
        return renderDadosView();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent maxH="90vh" bg="gray.50">
        {renderHeader()}
        <ModalCloseButton size="sm" />
        <ModalBody py={4}>{renderContent()}</ModalBody>
      </ModalContent>
    </Modal>
  );
}
